import React, { useState, useRef, useEffect } from 'react';
import { useChatContext } from '../hooks/useChat';

interface HomeViewProps {
  onNewChat: () => void;
  onViewHistory: () => void;
}

export default function HomeView({ onNewChat, onViewHistory }: HomeViewProps) {
  const { conversations, sendMessage, connectionState } = useChatContext();
  const hasHistory = conversations.length > 0;
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const disabled = connectionState !== 'connected';

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
    }
  }, [value]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (value.trim() && !disabled) {
      sendMessage(value.trim());
      setValue('');
      onNewChat(); // Navigate to chat view
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Centered content area */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-primary"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-1">
              How can we help?
            </h2>

            <p className="text-gray-500 text-sm">
              Ask me anything about your projects and issues.
            </p>
          </div>

          {/* Input area - centered with more space */}
          <form onSubmit={handleSubmit} className="w-full">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question..."
                disabled={disabled}
                rows={4}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ minHeight: '100px', maxHeight: '160px' }}
              />
              <button
                type="submit"
                disabled={!value.trim() || disabled}
                className="absolute bottom-3 right-3 p-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Press Enter to send, Shift+Enter for new line
            </p>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 space-y-2">
        {hasHistory && (
          <button
            onClick={onViewHistory}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            View {conversations.length} past conversation{conversations.length !== 1 ? 's' : ''}
          </button>
        )}

        <p className="text-[10px] text-gray-400 text-center">
          Powered by{' '}
          <a
            href="https://fanks.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Fanks
          </a>
        </p>
      </div>
    </div>
  );
}
